import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { ApiResponse } from '../../../_models/api-response.model';
import { UserList, UserSearchCriteria } from '../../../_models/user.model';
import { AdminMemberCard } from '../admin-member-card/admin-member-card';
import { ExcelExportService } from '../../../_services/app/excel-export.service'; 
import { PaginationComponent } from '../../../_shared_components/pagination/pagination.component';
import { CustomerAdminService } from '../../../_services/api/customer-admin.service';

@Component({
  selector: 'app-admin-members-list',
  imports: [CommonModule, FormsModule, AdminMemberCard, PaginationComponent],
  templateUrl: './admin-members-list.html',
  styleUrl: './admin-members-list.scss',
})
export class AdminMembersList implements OnInit, OnDestroy {
  showFilterPopup: boolean = false;
  selectedCategory: string = 'all';

  members: UserList[] = [];
  isLoading = false;
  currentPage = 0;
  pageSize = 10;
  totalCount = 0;
  totalPages = 0;
  
  searchTerm = '';
  private searchSubject = new Subject<string>();
  selectedVisibility: string = 'all';
  selectedSort: string = 'createdAt,desc';
  private destroy$ = new Subject<void>();

  constructor(
    private customerService: CustomerAdminService, 
    private excelExportService: ExcelExportService, 
    private router: Router,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.setupSearch();
    this.loadMembers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearch(): void {
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.currentPage = 0;
      this.loadMembers();
    });
  }

  onSearch(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onFilterChange(): void {
    this.currentPage = 0;
    this.loadMembers();
  }

  onSortChange(): void {
    this.currentPage = 0;
    this.loadMembers();
  }

  loadMembers(): void {
    this.isLoading = true;
    
    const criteria: UserSearchCriteria = {};
    if (this.searchTerm.trim()) {
      criteria.search = this.searchTerm.trim();
    }

    if (this.selectedVisibility !== 'all') {
      criteria.isEnabled = this.selectedVisibility === 'true';
    }

    const [sortField, sortDirection] = this.selectedSort.split(',');
    this.customerService.getAllCustomers(criteria, this.currentPage, this.pageSize, sortField, sortDirection)
      .subscribe({
        next: (response: ApiResponse<any>) => {
          this.members = response.data?.content || [];
          this.totalCount = response.data?.totalElements || 0;
          this.totalPages = response.data?.totalPages || 0;
          setTimeout(() => {
            this.isLoading = false;
          }, 500);
        },
        error: (error) => {
          console.error('Error loading members:', error);
          this.isLoading = false;
        }
      });
  }

  onMemberUpdated(): void {
    this.loadMembers();
  }

  onMemberDeleted(): void {
    this.loadMembers();
  }

  onPageChange(page: number) {
    this.isLoading = true;
    this.currentPage = page;
    if (isPlatformBrowser(this.platformId)) {
        window.scrollTo({top: 0, behavior: 'smooth'});
    }
    setTimeout(() => {
      this.loadMembers();
    }, 1000);
  }

  toggleFilterPopup() {
    this.showFilterPopup = !this.showFilterPopup;
  }

  closeFilterPopup(event: Event) {
    if ((event.target as HTMLElement).classList.contains('filter-overlay')) {
      this.showFilterPopup = false;
    }
  }

  exportMembers(): void {
    this.isLoading = true;
    const criteria: UserSearchCriteria = {};
    if (this.searchTerm.trim()) {
      criteria.search = this.searchTerm.trim();
    }

    if (this.selectedVisibility !== 'all') {
      criteria.isEnabled = this.selectedVisibility === 'true';
    }

    this.customerService.getAllCustomers(criteria, 0, 1000)
      .subscribe({
        next: (response: ApiResponse<any>) => {
          const allMembers = response.data?.content || [];
          this.generateExcelExport(allMembers);
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading members for export:', error);
          this.isLoading = false;
          alert('Erreur lors du chargement des membres pour l\'exportation');
        }
      });
  }

  private generateExcelExport(members: UserList[]): void {
    try {
      const fileName = this.excelExportService.generateFileName();
      const exportDate = new Date().toLocaleDateString('fr-BE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      
      const title = `Liste des membres Farco Security - Export du ${exportDate} (${members.length} membres)`;
      this.excelExportService.exportMembersToExcel(members, fileName, title);
    } catch (error) {
      console.error('Error generating Excel export:', error);
      alert('Erreur lors de la génération du fichier Excel');
    }
  }

  addClient(){
    this.router.navigate(['/admin/clients/create']);
  }
}